/**
 * DeltaReveal - Explicit Delta Sentence
 *
 * Full-width sentence describing the gap between asking price and floor.
 * Green for above floor, red for below floor, blue for above ceiling.
 */

import { motion } from 'framer-motion';

interface DeltaRevealProps {
  gutPrice: number;
  ttcp: number;
  ceiling: number;
  isVisible: boolean;
  delay?: number;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(cents) / 100);
}

export function DeltaReveal({
  gutPrice,
  ttcp,
  ceiling,
  isVisible,
  delay = 0
}: DeltaRevealProps) {
  const delta = gutPrice - ttcp;
  const isUnder = gutPrice < ttcp;
  const isOverCeiling = gutPrice > ceiling;

  let message: string;
  let colorClass: string;

  if (isOverCeiling) {
    message = `Your asking price of ${formatCurrency(gutPrice)} is ${formatCurrency(gutPrice - ceiling)} above the market ceiling.`;
    colorClass = 'text-blue-600';
  } else if (isUnder) {
    message = `Your asking price of ${formatCurrency(gutPrice)} is ${formatCurrency(Math.abs(delta))} below your floor.`;
    colorClass = 'text-red-600';
  } else {
    message = `Your asking price of ${formatCurrency(gutPrice)} is ${formatCurrency(delta)} above your floor.`;
    colorClass = 'text-emerald-600';
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
      transition={{
        delay: delay / 1000,
        duration: 0.4,
        ease: [0.34, 1.56, 0.64, 1],
      }}
      className="bg-white rounded-xl p-4 shadow-sm"
    >
      <p className={`text-sm font-medium leading-relaxed ${colorClass}`}>
        {message}
      </p>
    </motion.div>
  );
}

export default DeltaReveal;
